import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCheckbox,
  PrismIllustration,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { USER_ENGAGEMENT_ELEMENT_IDS } from 'lumen/models/seen-user-engagement-element';

interface ReplaceRangeWarningModalSignature {
  Args: {
    onCancel: () => void;
    onConfirm: () => void;
  };
}

export default class ReplaceRangeWarningModal extends Component<ReplaceRangeWarningModalSignature> {
  @service('user-engagement')
  declare userEngagementService: Services['user-engagement'];

  @tracked dontShowAgain = false;

  @action
  onCancel(): void {
    this.args.onCancel();
  }

  @action
  onConfirm(): void {
    if (this.dontShowAgain) {
      this.userEngagementService.markAsSeen(
        USER_ENGAGEMENT_ELEMENT_IDS.PRICE_RANGE_OVERRIDE_REPLACE_WARNING,
      );
    }
    this.args.onConfirm();
  }

  @action
  onDontShowAgainChange(event: Event): void {
    this.dontShowAgain = (event.target as HTMLInputElement).checked;
  }

  <template>
    <PrismModal @onClose={{this.onCancel}}>
      <:default>
        <div
          class="modal--small modal--padded mb-500 flex flex-col items-center text-center"
        >
          <PrismIllustration @name="100x100_info" @size="medium" />
          <div class="flex w-full flex-col gap-200">
            <h3>{{t
                "pricing_assistant.price_range_override.replace_warning.title"
              }}</h3>
            <p class="text-300--regular">{{t
                "pricing_assistant.price_range_override.replace_warning.description"
              }}</p>
          </div>
        </div>
      </:default>
      <:footer>
        <div class="mx-500 flex h-full flex-row items-center justify-between">
          <PrismCheckbox
            @checked={{this.dontShowAgain}}
            @onChange={{this.onDontShowAgainChange}}
          >
            {{t "general.dont_show_again"}}
          </PrismCheckbox>
          <div class="flex gap-200">
            <PrismButton
              @buttonType="secondary"
              @label={{t "general.cancel"}}
              @onClick={{this.onCancel}}
            />
            <PrismButton
              @buttonType="primary"
              @iconBefore="send"
              @label={{t "pricing_assistant.label.push"}}
              @onClick={{this.onConfirm}}
            />
          </div>
        </div>
      </:footer>
    </PrismModal>
  </template>
}
